import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Upload, Trash2, Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function StockIssue() {
  const [items, setItems] = useState([]);
  const [issues, setIssues] = useState([]);
  const [projects, setProjects] = useState([]);
  const [itemSearchOpen, setItemSearchOpen] = useState(false);
  const [projectSearchOpen, setProjectSearchOpen] = useState(false);
  const [formData, setFormData] = useState({
    item_id: '',
    quantity: '',
    employee_name: '',
    date: new Date().toISOString().split('T')[0],
    project: ''
  });

  useEffect(() => {
    fetchItems();
    fetchIssues();
    fetchProjects();
  }, []);

  const fetchItems = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/items`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setItems(response.data);
    } catch (error) {
      toast.error('Failed to fetch items');
    }
  };

  const fetchIssues = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/stock-issues`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIssues(response.data);
    } catch (error) {
      toast.error('Failed to fetch issues');
    }
  };

  const fetchProjects = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/projects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProjects(response.data);
    } catch (error) {
      toast.error('Failed to fetch projects');
    }
  };

  const handleItemSelect = (itemId) => {
    setFormData({ ...formData, item_id: itemId });
    setItemSearchOpen(false);
  };

  const handleProjectSelect = (westawProjectNumber) => {
    setFormData({ ...formData, project: westawProjectNumber });
    setProjectSearchOpen(false);
  };

  const getSelectedItemName = () => {
    const selectedItem = items.find(i => i.id === formData.item_id);
    return selectedItem ? `${selectedItem.name} (${selectedItem.category})` : 'Search and select an item...';
  };

  const getSelectedProjectDisplay = () => {
    const selectedProject = projects.find(p => p.westaw_project_number === formData.project);
    return selectedProject ? `${selectedProject.westaw_project_number} - ${selectedProject.client_name}` : 'Search and select a project...';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...formData,
        date: new Date(formData.date).toISOString(),
        quantity: parseFloat(formData.quantity)
      };

      await axios.post(`${BACKEND_URL}/api/stock-issues`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Stock issue created successfully');
      setFormData({
        item_id: '',
        quantity: '',
        employee_name: '',
        date: new Date().toISOString().split('T')[0],
        project: ''
      });
      fetchIssues();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create issue');
    }
  };

  const handleDelete = async (issueId) => {
    if (!window.confirm('Are you sure you want to delete this stock issue?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/stock-issues/${issueId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Stock issue deleted successfully');
      fetchIssues();
    } catch (error) {
      toast.error('Failed to delete stock issue');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-4xl font-extrabold text-slate-900 tracking-tight font-heading" data-testid="stock-issue-title">Stock Issue</h1>
        <p className="text-slate-600 mt-2">Record items issued to employees</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Form */}
        <Card className="bg-white border-slate-200 shadow-sm">
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="text-xl font-semibold">New Issue</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="issue-form">
              <div className="space-y-2">
                <Label htmlFor="item">Item</Label>
                <Popover open={itemSearchOpen} onOpenChange={setItemSearchOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={itemSearchOpen}
                      className="w-full justify-between font-normal"
                      data-testid="issue-item-select"
                    >
                      <span className={formData.item_id ? "text-slate-900" : "text-slate-500"}>
                        {getSelectedItemName()}
                      </span>
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Search items by name or category..." />
                      <CommandList>
                        <CommandEmpty>No item found.</CommandEmpty>
                        <CommandGroup>
                          {items.map((item) => (
                            <CommandItem
                              key={item.id}
                              value={`${item.name} ${item.category}`}
                              onSelect={() => handleItemSelect(item.id)}
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  formData.item_id === item.id ? "opacity-100" : "opacity-0"
                                )}
                              />
                              <div className="flex flex-col">
                                <span className="font-medium">{item.name}</span>
                                <span className="text-xs text-slate-500">{item.category} • {item.unit}</span>
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label htmlFor="quantity">Quantity</Label>
                <Input
                  id="quantity"
                  data-testid="issue-quantity-input"
                  type="number"
                  step="0.01"
                  value={formData.quantity}
                  onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="employee_name">Employee Name</Label>
                <Input
                  id="employee_name"
                  data-testid="issue-employee-input"
                  value={formData.employee_name}
                  onChange={(e) => setFormData({ ...formData, employee_name: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="date">Date</Label>
                <Input
                  id="date"
                  data-testid="issue-date-input"
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="project">WESTAW Project</Label>
                <Popover open={projectSearchOpen} onOpenChange={setProjectSearchOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={projectSearchOpen}
                      className="w-full justify-between font-normal"
                      data-testid="issue-project-select"
                    >
                      <span className={formData.project ? "text-slate-900" : "text-slate-500"}>
                        {getSelectedProjectDisplay()}
                      </span>
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Search projects..." />
                      <CommandList>
                        <CommandEmpty>No project found.</CommandEmpty>
                        <CommandGroup>
                          {projects.map((project) => (
                            <CommandItem
                              key={project.id}
                              value={`${project.westaw_project_number} ${project.client_name} ${project.client_project_number}`}
                              onSelect={() => handleProjectSelect(project.westaw_project_number)}
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  formData.project === project.westaw_project_number ? "opacity-100" : "opacity-0"
                                )}
                              />
                              <div className="flex flex-col">
                                <span className="font-medium">{project.westaw_project_number}</span>
                                <span className="text-xs text-slate-500">{project.client_name} • {project.client_project_number}</span>
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>

              <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800" data-testid="submit-issue-button">
                <Upload className="w-4 h-4 mr-2" />
                Record Issue
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Recent Issues */}
        <Card className="bg-white border-slate-200 shadow-sm">
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="text-xl font-semibold">Recent Issues</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-3 max-h-[600px] overflow-y-auto" data-testid="recent-issues-list">
              {issues.slice(0, 10).map((issue) => (
                <div key={issue.id} className="p-4 bg-slate-50 border border-slate-200 rounded-lg" data-testid={`issue-${issue.id}`}>
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1">
                      <p className="font-semibold text-slate-900">{issue.item_name}</p>
                      <p className="text-sm text-slate-600">{issue.employee_name}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-red-700 bg-red-50 px-2 py-1 rounded">
                        -{issue.quantity}
                      </span>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(issue.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`delete-issue-${issue.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs text-slate-600">
                    <div>Project: {issue.project}</div>
                    <div className="text-right">Date: {new Date(issue.date).toLocaleDateString()}</div>
                  </div>
                </div>
              ))}
              {issues.length === 0 && (
                <div className="text-center py-8 text-slate-600">
                  No issues recorded yet
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default StockIssue;
